/**
 * The `/dossiers` route's URL search shape, plus its default (unfiltered)
 * value. Lives in a plain, non-route module — rather than being exported from
 * `routes/_authenticated/dossiers.tsx` — so other features (e.g. the
 * dashboard's KPI shortcuts) can build a fully-typed search object for
 * `navigate({ to: "/dossiers", search })` without importing the route module
 * itself. Importing a route file from another route creates a runtime
 * dependency between them, which can defeat route-level code splitting and
 * couples the importer to that route's implementation.
 */

import type { EndOfContract } from "@/dossiers/endOfContractFilter";
import {
  DOSSIER_SORT_FIELDS,
  type DossierSortDirection,
  type DossierSortField,
} from "@/dossiers/dossierOverviewLayout";

/** A relative creation-date window used to filter the dossiers list. */
export type Period = "all" | "7" | "30" | "90";

export interface DossiersSearch {
  readonly page: number;
  /** Scopes the list to archived dossiers instead of active ones. */
  readonly archived: boolean;
  /** Scopes the list to dossiers no admin has opened yet. */
  readonly unread: boolean;
  /** Scopes the list to dossiers with at least one open (not yet completed) WorkQueue task. */
  readonly actionRequired: boolean;
  /** Scopes the list to dossiers created within a relative period. */
  readonly period: Period;
  /** Scopes the list to a single service partner (hersteller), by id. */
  readonly servicePartnerId: string | undefined;
  /** Scopes the list to a single opdrachtgever (insurer or lease company), by id. */
  readonly companyId: string | undefined;
  /** Scopes the list to dossiers assigned to a single behandelaar (user), by id. */
  readonly assigneeId: string | undefined;
  /** Scopes the list to dossiers lying with a single team (user collection), by id. */
  readonly userCollectionId: string | undefined;
  /** Scopes the list to dossiers whose status is any of these values (by name). */
  readonly status: readonly string[];
  /**
   * Three-state insurance filter: `undefined` is "no choice" (no filter sent),
   * `true` is externally insured, `false` is internally insured. Unlike the
   * two-state booleans above, `false` is a deliberate choice the backend acts
   * on, so it must never be collapsed into "unset".
   */
  readonly externalInsured: boolean | undefined;
  /** Three-state claimability (verhaalbaarheid) filter — same semantics as `externalInsured`. */
  readonly claimable: boolean | undefined;
  /** Scopes the list to dossiers whose vehicle brand is any of these values. */
  readonly brand: readonly string[];
  /** Three-state selection; rule IDs are resolved from the served layout. */
  readonly endOfContract: EndOfContract | undefined;
  /** Scopes the list to one cause of damage (a `CauseOfDamage` enum value). */
  readonly causeOfDamage: string | undefined;
  /** Scopes the list to one dossier type (a `DossierType` discriminator value). */
  readonly dossierType: string | undefined;
  /** Scopes the list to one repair type (a `RepairType` enum value). */
  readonly repairType: string | undefined;
  /**
   * The column the list is sorted by. `undefined` (unlike every filter above)
   * rather than defaulted here: which column and direction "no explicit
   * choice" resolves to is the affiliate's configured default
   * (`layout.sorting`, fetched over the network), which this pure,
   * URL-only function cannot see. The route resolves the effective sort as
   * `sortBy ?? layout.sorting?.defaultColumn`. Once a user clicks a header,
   * both fields are always written together, so a shared link is
   * self-describing.
   */
  readonly sortBy: DossierSortField | undefined;
  readonly sortDirection: DossierSortDirection | undefined;
}

/**
 * The default, unfiltered `/dossiers` search — the active scope with no other
 * filters applied. Other features build a fully-typed search object by
 * spreading this and overriding only the fields relevant to their shortcut,
 * instead of duplicating every field of {@link DossiersSearch}.
 *
 * Frozen (including its filter arrays) so every importer shares
 * the same immutable default rather than risking one caller mutating an array
 * another caller also spreads in.
 */
export const DEFAULT_DOSSIERS_SEARCH: DossiersSearch = Object.freeze({
  page: 1,
  archived: false,
  unread: false,
  actionRequired: false,
  period: "all",
  servicePartnerId: undefined,
  companyId: undefined,
  assigneeId: undefined,
  userCollectionId: undefined,
  status: Object.freeze([]),
  externalInsured: undefined,
  claimable: undefined,
  brand: Object.freeze([]),
  endOfContract: undefined,
  causeOfDamage: undefined,
  dossierType: undefined,
  repairType: undefined,
  sortBy: undefined,
  sortDirection: undefined,
});

const SORT_FIELD_VALUES: ReadonlySet<string> = new Set<DossierSortField>(
  DOSSIER_SORT_FIELDS,
);

/** Coerces an unknown search param into a valid sort field, or `undefined` for anything else. */
export function coerceSortBy(value: unknown): DossierSortField | undefined {
  return typeof value === "string" && SORT_FIELD_VALUES.has(value)
    ? (value as DossierSortField)
    : undefined;
}

/** Coerces an unknown search param into a valid sort direction, or `undefined` for anything else. */
export function coerceSortDirection(
  value: unknown,
): DossierSortDirection | undefined {
  return value === "ASC" || value === "DESC" ? value : undefined;
}
